/**
 * The geometry of a surface, kept free of React and the DOM so it can be
 * reasoned about (and tested) on its own.
 */

/** How a surface is anchored before it is dragged. */
export type SurfacePlacement =
	/** Centered on the viewport, dragging moves it away from the center. */
	| 'center'
	/** Anchored to the viewport origin, dragging moves it in absolute pixels. */
	| 'origin';

export type SurfaceSize = { width: number; height: number };

export type SurfaceViewport = { width: number; height: number };

export type SurfaceOffset = { x: number; y: number };

export type SurfaceRange = {
	minX: number;
	maxX: number;
	minY: number;
	maxY: number;
};

export function clampValue(value: number, min: number, max: number) {
	if (max < min) return min;
	return Math.min(Math.max(value, min), max);
}

/**
 * Where a surface starts: centered in the viewport, then pushed by the
 * offset it was given.
 */
export function initialPosition({
	placement,
	offset,
	size,
	viewport,
	padding,
}: {
	placement: SurfacePlacement;
	offset: SurfaceOffset;
	size: SurfaceSize;
	viewport: SurfaceViewport;
	padding: number;
}): SurfaceOffset {
	if (placement === 'origin') {
		return {
			x: clampValue(
				(viewport.width - size.width) / 2 + offset.x,
				0,
				viewport.width - size.width - padding,
			),
			y: clampValue(
				(viewport.height - size.height) / 2 + offset.y,
				0,
				viewport.height - size.height - padding,
			),
		};
	}

	return { x: offset.x, y: offset.y };
}

/**
 * The range a surface may be dragged within. A centered surface moves
 * around the middle of the viewport, an anchored one stays inside it.
 *
 * For 'origin' placement, allows dragging off-screen while keeping a
 * minimum visible portion on each edge:
 * - Top: can go completely off (current behavior)
 * - Left/Right/Bottom: at least `minVisible` pixels remain visible
 */
export function dragRange({
	placement,
	size,
	viewport,
	padding,
	minVisible = 100,
}: {
	placement: SurfacePlacement;
	size: SurfaceSize;
	viewport: SurfaceViewport;
	padding: number;
	minVisible?: number;
}): SurfaceRange {
	if (placement === 'origin') {
		return {
			// Allow dragging left until only minVisible pixels remain
			minX: -(size.width - minVisible),
			// Allow dragging right until only minVisible pixels remain
			maxX: viewport.width - minVisible,
			// Allow dragging completely off top (current behavior)
			minY: 0,
			// Allow dragging down until only minVisible pixels remain
			maxY: viewport.height - minVisible,
		};
	}

	const roomX = (viewport.width - size.width) / 2 - padding;
	const roomY = (viewport.height - size.height) / 2 - padding;

	return { minX: -roomX, maxX: roomX, minY: -roomY, maxY: roomY };
}

/** Pulls a position back inside the range. */
export function clampPosition(
	position: SurfaceOffset,
	range: SurfaceRange,
): SurfaceOffset {
	return {
		x: clampValue(position.x, range.minX, range.maxX),
		y: clampValue(position.y, range.minY, range.maxY),
	};
}
